import * as React from "react";
import {useEffect, useImperativeHandle, useState} from "react";
import {Trash2, RotateCcw} from "lucide-react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import { toast } from "sonner";
import {MigrationStatus} from "../../models/MigrationStatus";
import MigrationStatusChip from "../common/MigrationStatusChip";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import JobsRepository from "../../repositories/JobsRepository";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../common/ActionConfirmationModal";
import TextHighlighter from "../common/TextHighlighter";
import {useTableHandlers} from "../common/Table/Hooks/UseTableHandlers";
import {useTableFileFilters} from "../common/Table/Hooks/UseTableFileFilters";
import {QUERY_KEYS} from "../../constants/QueryKeys";
import {NumberInput} from "../common/Table/NumberInput";
import {FileStatusFilter} from "../common/Table/File/FileStatusFilter";
import {FileExtensionsFilter} from "../common/Table/File/FileExtensionsFilter";
import {SearchInput} from "../common/Table/SearchInput";
import TablePagination from "../common/Table/TablePagination";
import {QUERY_CONFIG} from "../../constants/QueryConfig";
import { FirmDocument } from "../../models/FirmDocumentFolder/FirmDocument";
import FirmDocumentsFoldersRepository from "../../repositories/FirmDocumentsFoldersRepository";
import { ActiveJobsExplorer } from "../../models/Jobs/ActiveJobsExplorer";
import { FileFailureTypeFilter } from "../common/Table/File/FileFailureTypeFilter";
import {FileFailureTypeChip} from "../common/Table/File/FileFailureTypeChip";
import {FileConflictFilter} from "../common/Table/File/FileConflictFilter";
import {FileConflictChip} from "../common/Table/File/FileConflictChip";
import {cn} from "@/lib/utils";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import {
  fileFiltersBoxClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import {Button} from "@/components/ui/button";
import {Badge} from "@/components/ui/badge";
import TableEmptyState from "../common/TableEmptyState";

interface FirmDocumentsTableProps {
  organizationId: string;
  activeJobsExplorer: ActiveJobsExplorer;
  newFirmDocumentsForDeletion: FirmDocument[];
  setNewFirmDocumentsForDeletion: React.Dispatch<React.SetStateAction<FirmDocument[]>>;
  firmDocumentsFolderId?: string;
  disabled?: boolean;
  toolbar?: React.ReactNode;
}

export interface FirmDocumentsTableRef {
  exportCurrentViewToCsv: (reason?: string) => void;
}

export function FirmDocumentsTable({
  ref,
  organizationId,
  activeJobsExplorer,
  newFirmDocumentsForDeletion,
  setNewFirmDocumentsForDeletion,
  firmDocumentsFolderId,
  disabled,
  toolbar
}: FirmDocumentsTableProps & { ref?: React.Ref<FirmDocumentsTableRef> }) {
  const firmDocumentsFoldersRepository = new FirmDocumentsFoldersRepository();
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const [firmDocuments, setFirmDocuments] = useState<FirmDocument[]>([]);
  const [conflictReasonFilter, setConflictReasonFilter] = React.useState<string | null>(null);
  const [conflictStatusFilter, setConflictStatusFilter] = React.useState<string | null>(null);

  const { order, sort, page, rowsPerPage, rows, setRows, setPage, changeSort, changeOrder, handleRowsPerPageChange } = useTableHandlers({ order: "asc", sort: "status", page: 0, rowsPerPage: 100 });
  const { statusFilter, failureType, filePathFilter, destinationPathFilter, minSizeFilter, maxSizeFilter, extensions, extensionsSearch, setStatusFilter, setFailureType, setExtensions, setExtensionsSearch, handleDestinationPathSearch, handleFilePathSearch, handleMaxSizeFilter, handleMinSizeFilter } = useTableFileFilters();

  const firmDocumentsQuery = useQuery({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS, organizationId, firmDocumentsFolderId, page, rowsPerPage, statusFilter, failureType, sort, order, extensionsSearch, filePathFilter, destinationPathFilter, minSizeFilter, maxSizeFilter, conflictReasonFilter, conflictStatusFilter], queryFn:
    async () => await firmDocumentsFoldersRepository.getFirmDocumentsForFirmDocumentsFolder(firmDocumentsFolderId, {
      descending: order === "desc",
      orderBy: sort,
      minFileSize: minSizeFilter,
      maxFileSize: maxSizeFilter,
      filePath: filePathFilter,
      destinationPath: destinationPathFilter,
      pageNumber: page + 1,
      pageSize: rowsPerPage,
      failureType: failureType,
      status: statusFilter ? [statusFilter] : null,
      extension: extensionsSearch.length > 0 ? extensionsSearch : null,
      conflictReason: conflictReasonFilter ? [conflictReasonFilter] : null,
      conflictStatus: conflictStatusFilter ? [conflictStatusFilter] : null,
    }), staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME, refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL, refetchIntervalInBackground: true, refetchOnMount: true, });

  useEffect(() => {
    if (firmDocumentsQuery.data?.data) {
      setFirmDocuments(firmDocumentsQuery.data?.data);
      setRows(firmDocumentsQuery.data.paging?.totalCount ?? 0);
    }
  }, [firmDocumentsQuery]);

  const exportCurrentViewToCsv = async (reason?: string) => {
    const options = {
      descending: order === "desc",
      orderBy: sort,
      minFileSize: minSizeFilter,
      maxFileSize: maxSizeFilter,
      filePath: filePathFilter,
      destinationPath: destinationPathFilter,
      failureType: failureType,
      status: statusFilter ? [statusFilter] : null,
      extension: extensionsSearch.length > 0 ? extensionsSearch : null,
      conflictReason: conflictReasonFilter ? [conflictReasonFilter] : null,
      conflictStatus: conflictStatusFilter ? [conflictStatusFilter] : null,
    };
    try {
      await jobsRepository.startCsvGenerationFromFirmDocuments(organizationId, options, reason, firmDocumentsFolderId);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_FIRM_DOCUMENTS, firmDocumentsFolderId] });
      toast.success("Firm documents export started");
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to start firm documents export", { duration: 8000 });
    }
  };

  useImperativeHandle(ref, () => ({ exportCurrentViewToCsv }));

  const handleMarkNotFoundFileForRetry = async (firmDocumentsFolderId: string, fileId: string, reason: string) => {
    await firmDocumentsFoldersRepository.markNotFoundFileForRetry(firmDocumentsFolderId, fileId, reason);
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS] });
  }

  const handleAddFirmDocumentForDeletion = (firmDocumentEntity: FirmDocument) => {
    if (!disabledRows.has(firmDocumentEntity.id))
      setNewFirmDocumentsForDeletion((prevRows) => [...prevRows, firmDocumentEntity]);
  };

  const getDisabledRows = () => {
    switch (activeJobsExplorer) {
      case ActiveJobsExplorer.Delete:
        return new Set(newFirmDocumentsForDeletion.map((c) => c.id));
    }
    return new Set<string>();
  };

  const getActiveButtonForExplorer = (firmDocumentEntity: FirmDocument, isDisabled: boolean) => {
    switch (activeJobsExplorer) {
      case ActiveJobsExplorer.Delete:
        return (
          <Button variant="ghost" size="icon-sm" className="text-destructive" aria-label="Delete" disabled={isDisabled || disabled} onClick={() => handleAddFirmDocumentForDeletion(firmDocumentEntity)}>
            <Trash2 className="size-4" />
          </Button>
        );
      default:
        <></>;
    }
  };

  const disabledRows = getDisabledRows();
  const loading = firmDocumentsQuery.isFetching || firmDocumentsQuery.isError;

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      {toolbar}
      <div className={fileFiltersBoxClassName}>
        <FileExtensionsFilter extensions={extensions} onExtensionsChange={setExtensions} onSearch={() => setExtensionsSearch(extensions)} />
        <NumberInput label="Files min size" placeholder="Min size" onBlur={(e) => handleMinSizeFilter(e.target.value)} />
        <NumberInput label="Files max size" placeholder="Max size" onBlur={(e) => handleMaxSizeFilter(e.target.value)} />
        <FileStatusFilter value={statusFilter} onChange={(newValue) => { setPage(0); setStatusFilter(newValue); }} />
        <FileFailureTypeFilter value={failureType} onChange={(newValue) => { setPage(0); setFailureType(newValue); }} />
        <FileConflictFilter reason={conflictReasonFilter} status={conflictStatusFilter} onReasonChange={(newValue) => { setPage(0); setConflictReasonFilter(newValue); }} onStatusChange={(newValue) => { setPage(0); setConflictStatusFilter(newValue); }} />
      </div>
      <div className={fileFiltersBoxClassName}>
        <SearchInput label="Source path / API ID" placeholder="File path or source API ID" onBlur={(e) => handleFilePathSearch(e.target.value)} />
        <SearchInput label="Destination path" placeholder="SharePoint path" onBlur={(e) => handleDestinationPathSearch(e.target.value)} />
      </div>
      <div className={cn(tableWrapperClassName, loading && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              {firmDocumentsFolderId === undefined && (<TableHead className="w-[12%]"><SortableTableColumnHeader label={"Folder ID"} sortProperty={"firmDocumentsFolderId"} getSort={() => sort} setSort={(newSort) => changeSort(newSort)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>)}
              <TableHead className="w-[13%]"><SortableTableColumnHeader label={"Status"} sortProperty={"status"} getSort={() => sort} setSort={(newSort) => changeSort(newSort)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead className="w-[16%]"><SortableTableColumnHeader label={"Failure Reason"} sortProperty={"failureType"} getSort={() => sort} setSort={(newSort) => changeSort(newSort)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead className="w-[12%]">Conflict</TableHead>
              <TableHead className="w-[21%]">File Path / Source Api File ID</TableHead>
              <TableHead className="w-[21%]">Destination Path</TableHead>
              <TableHead className="w-[9%]"><SortableTableColumnHeader label={"Size (MB)"} sortProperty={"fileSize"} getSort={() => sort} setSort={(newSort) => changeSort(newSort)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead className="w-[8%]">Extension</TableHead>
              <TableHead className="w-[50px]" />
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {firmDocuments.map((firmDocument: FirmDocument) => {
              const isDisabled = disabledRows.has(firmDocument.id);
              return (
                <TableRow key={firmDocument.id}>
                  {firmDocumentsFolderId === undefined && (<TableCell className="whitespace-normal break-all"><a href={`/organization/${organizationId}/firm-documents-folder/${firmDocument.firmDocumentsFolderId}`} className="underline hover:no-underline">{firmDocument.firmDocumentsFolderId}</a></TableCell>)}
                  <TableCell><MigrationStatusChip status={firmDocument.status} isFile /></TableCell>
                  <TableCell><FileFailureTypeChip failureType={firmDocument.failureType} failureReason={firmDocument.failureReason} /></TableCell>
                  <TableCell><FileConflictChip conflictReason={firmDocument.conflictReason} conflictStatus={firmDocument.conflictStatus} /></TableCell>
                  <TableCell className="whitespace-normal break-all"><TextHighlighter text={firmDocument.filePath} searchTerm={filePathFilter} level="body-sm" /></TableCell>
                  <TableCell className="whitespace-normal break-all"><TextHighlighter text={firmDocument.destinationPath} searchTerm={destinationPathFilter} level="body-sm" /></TableCell>
                  <TableCell><Badge variant="neutral">{firmDocument.fileSizeInMb.toFixed(2)}</Badge></TableCell>
                  <TableCell><Badge variant="primary">{firmDocument.extension ?? "-"}</Badge></TableCell>
                  <TableCell>{getActiveButtonForExplorer(firmDocument, isDisabled)}</TableCell>
                  <TableCell>
                    {firmDocument.status === MigrationStatus.NotFound && (
                      <ActionConfirmationModal actionConfig={ACTION_CONFIGS.MARK_NOT_FOUND_FILE_FOR_RETRY} onConfirm={async (reason) => await handleMarkNotFoundFileForRetry(firmDocumentsFolderId === undefined ? firmDocument.firmDocumentsFolderId : firmDocumentsFolderId, firmDocument.id, reason)} buttonProps={{ disabled: isDisabled || disabled, size: "sm", color: "primary"  }} tooltip="Mark for retry"
                        >
                          <Button variant="ghost" size="icon-sm" className="text-primary" aria-label="MarkForRetry"><RotateCcw className="size-4" /></Button>
                      </ActionConfirmationModal>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
              {firmDocuments.length === 0 && !firmDocumentsQuery.isFetching && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={99}>
                    <TableEmptyState title="No firm documents found" />
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
        <TablePagination page={page} rowsPerPage={rowsPerPage} rows={rows} onPageChange={setPage} onRowsPerPageChange={handleRowsPerPageChange} />
      </div>
    </div>
  );
};
